/**
 * Smart Chat Input Component
 * Intelligent input with autocomplete and query suggestions
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input, Button } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import './SmartChatInput.css';

const { TextArea } = Input;

interface Suggestion {
  type: 'keyword' | 'template' | 'entity';
  text: string;
  insertText: string;
  description?: string;
}

interface SmartChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
}

// Common query templates and keywords
const QUERY_TEMPLATES: Suggestion[] = [
  {
    type: 'template',
    text: 'Koliko imam',
    insertText: 'Koliko imam aktivnih ',
    description: 'Count entities',
  },
  {
    type: 'template',
    text: 'Pokaži mi',
    insertText: 'Pokaži mi ',
    description: 'Show entities',
  },
  {
    type: 'template',
    text: 'Kreiraj',
    insertText: 'Kreiraj novi ',
    description: 'Create entity',
  },
  {
    type: 'template',
    text: 'Pronađi',
    insertText: 'Pronađi ',
    description: 'Search entities',
  },
  {
    type: 'template',
    text: 'Dodaj',
    insertText: 'Dodaj ',
    description: 'Add something',
  },
  {
    type: 'template',
    text: 'Dodeli',
    insertText: 'Dodeli ',
    description: 'Assign entity',
  },
  {
    type: 'template',
    text: 'Promeni',
    insertText: 'Promeni ',
    description: 'Update entity',
  },
  {
    type: 'keyword',
    text: 'tiket',
    insertText: 'tiket ',
    description: 'Ticket keyword',
  },
  {
    type: 'keyword',
    text: 'lead',
    insertText: 'lead ',
    description: 'Lead keyword',
  },
  {
    type: 'keyword',
    text: 'board',
    insertText: 'board ',
    description: 'Kanban board keyword',
  },
  {
    type: 'keyword',
    text: 'event',
    insertText: 'event ',
    description: 'Calendar event keyword',
  },
  {
    type: 'keyword',
    text: 'urgent',
    insertText: 'urgentni ',
    description: 'Urgent filter',
  },
  {
    type: 'keyword',
    text: 'high priority',
    insertText: 'high priority ',
    description: 'Priority filter',
  },
  {
    type: 'keyword',
    text: 'moji',
    insertText: 'moji ',
    description: 'My items filter',
  },
];

export const SmartChatInput: React.FC<SmartChatInputProps> = ({
  value,
  onChange,
  onSend,
  disabled,
  placeholder = 'Ask me anything...',
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<any>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get suggestions based on input
  const suggestions = useMemo(() => {
    if (!value.trim()) return [];

    const term = value.toLowerCase().trim();
    const lastWord = term.split(' ').pop() || '';

    if (lastWord.length < 2) return [];

    return QUERY_TEMPLATES.filter(
      (suggestion) =>
        suggestion.text.toLowerCase().includes(lastWord) ||
        suggestion.insertText.toLowerCase().includes(lastWord)
    ).slice(0, 5); // Limit to 5 suggestions
  }, [value]);

  // Show/hide suggestions
  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && value.trim().length >= 2);
    setSelectedIndex(0);
  }, [suggestions, value]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;

      case 'Enter':
        if (!e.shiftKey) {
          e.preventDefault();
          if (suggestions[selectedIndex]) {
            applySuggestion(suggestions[selectedIndex]);
          } else {
            onSend();
          }
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
    }
  };

  const applySuggestion = (suggestion: Suggestion) => {
    // Replace the last word with the suggestion
    const words = value.split(' ');
    words[words.length - 1] = suggestion.insertText;
    onChange(words.join(' '));
    setShowSuggestions(false);

    // Focus back to input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <div className="smart-chat-input-wrapper">
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="autocomplete-suggestions" ref={suggestionsRef}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => applySuggestion(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-main">
                <span className="suggestion-type-badge">{suggestion.type}</span>
                <span className="suggestion-text">{suggestion.text}</span>
              </div>
              {suggestion.description && (
                <span className="suggestion-description">
                  {suggestion.description}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div className="smart-chat-input">
        <TextArea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={disabled}
          className="smart-textarea"
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={onSend}
          disabled={!value.trim() || disabled}
          className="smart-send-btn"
        />
      </div>
    </div>
  );
};
